import { create } from 'zustand'
import {
  computeWaterfallSchedule,
  waterfallScheduleRequestFromPolicy,
  parseApiCashPolicySplitSchedule,
  createCase,
  deleteCase as deleteCaseApi,
  formatApiError,
  getCase,
  getHistory,
  getModel,
  getNotes,
  getSharedNotes,
  getYieldCurve,
  importModel,
  revertLatestImport,
  rollForwardCases,
  isConcurrencyConflict,
  isImportBlocked,
  isImportDrift,
  isValidationError,
  listCases,
  listCaseSnapshots,
  listExports,
  toApiInputs,
  toApiKpis,
  toApiOutputs,
  toEngineInputs,
  updateCase,
  upsertNote as upsertNoteApi,
  upsertSharedNote as upsertSharedNoteApi,
  type CaseDetail,
  type CaseListItem,
  type ImportResultResponse,
  type ModelSummaryResponse,
  type RollForwardSummary,
  type ProjectionExportListItem,
  type SnapshotRow,
  type ValidationItem,
} from '@/services/projections'
import { buildCompareModel, type CompareModel } from '@/pages/projections/compare/compareModel'
import { BOOKKEEPER_APP_KEY } from '@/tableControls'
import {
  emptyInputs,
  evaluate,
  loadSchema,
  materialize,
  parseNumeric,
  type CaseInputs,
  type CashPolicy,
  type DriverParams,
  type DriverTypeInput,
  type EvaluateContext,
  type History,
  type Model,
  type OneTimeItem,
  type ValidationOptions,
  type ValidationThresholds,
  type View,
} from '@/pages/projections/engine'
import { DEFAULT_THRESHOLDS } from '@/pages/projections/validationCodes'
import { historyFromApiPayload } from '@/pages/projections/historyFromApi'
import {
  ignoreGroupForLine,
  isGroupIgnored,
} from '@/pages/projections/engine/ignoreGroups'
import type { PickerDriver } from '@/pages/projections/gridClasses'
import type { ApiHistoryPayload } from '@/pages/projections/historyTypes'
import { isSharedNoteCellKey, methodologyCellKey } from '@/pages/projections/cellKeys'
import { monthLabel } from '@/pages/projections/monthColumns'
import { formatMonthLabel } from '@/pages/projections/projectionsFormat'
import { todayIsoDate } from '@/utils/waterfallCalculator'
import type { ParsedCaseFile } from '@/pages/projections/caseJson'
import { materialise, solveAnchorValue } from '@/pages/projections/caseJson/recipe'
import { anchorFromHistory, resolveSeasonalityFactors } from '@/pages/projections/caseJson/recipeHistory'
import { projectionMonthKeys } from '@/pages/projections/caseJson/utils'
import { monthKeyFromT } from '@/pages/projections/engine/calendar'
import { driverInputUnit } from '@/pages/projections/driverUtils'
import type { DriverType } from '@/pages/projections/engine/types'
import { hydrateCashPolicy } from '@/pages/projections/cashPolicy/hydratePolicy'
import { effectiveOneTime } from '@/pages/projections/engine/cashPolicy'
import { dismissToast, showToast, toast } from '@/store/toastStore'

export type ProjectionsView = View | 'one_time_items'
export type HistoryMonths = 3 | 6 | 12 | 24 | 'all'

export type PickerDriverType = PickerDriver | null

export const PROJECTIONS_HISTORY_MONTHS_KEY = `${BOOKKEEPER_APP_KEY}:projections:historyMonths`
export const PROJECTIONS_SELECTED_CASE_ID_KEY = `${BOOKKEEPER_APP_KEY}:projections:selectedCaseId`
export const PROJECTIONS_COMPARE_MODE_KEY = `${BOOKKEEPER_APP_KEY}:projections:compareMode`

export type CompareMode = 'off' | 'on'
export type CompareColumns = 'all' | string[]

export function readCompareMode(): CompareMode {
  if (typeof localStorage === 'undefined') {
    return 'off'
  }
  return localStorage.getItem(PROJECTIONS_COMPARE_MODE_KEY) === 'on' ? 'on' : 'off'
}

function writeCompareMode(mode: CompareMode): void {
  if (typeof localStorage === 'undefined') {
    return
  }
  localStorage.setItem(PROJECTIONS_COMPARE_MODE_KEY, mode)
}

export function readPersistedSelectedCaseId(): number | null {
  if (typeof localStorage === 'undefined') {
    return null
  }
  const raw = localStorage.getItem(PROJECTIONS_SELECTED_CASE_ID_KEY)
  if (!raw) {
    return null
  }
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : null
}

function writePersistedSelectedCaseId(id: number | null): void {
  if (typeof localStorage === 'undefined') {
    return
  }
  if (id == null) {
    localStorage.removeItem(PROJECTIONS_SELECTED_CASE_ID_KEY)
  } else {
    localStorage.setItem(PROJECTIONS_SELECTED_CASE_ID_KEY, String(id))
  }
}

export type SaveResult =
  | { ok: true; case: CaseDetail }
  | { ok: false; kind: 'validation'; errors: ValidationItem[]; warnings: ValidationItem[] }
  | { ok: false; kind: 'concurrency'; case: CaseDetail }
  | { ok: false; kind: 'error'; message: string }

export type ImportResult =
  | { ok: true; result: ImportResultResponse }
  | { ok: false; kind: 'blocked'; control_status: string; detail: string }
  | { ok: false; kind: 'drift'; diff: unknown }
  | { ok: false; kind: 'error'; message: string }

export type CaseMetaResult =
  | { ok: true; case: CaseDetail }
  | { ok: false; kind: 'concurrency'; case: CaseDetail }
  | { ok: false; kind: 'error'; message: string }

export type CreateCaseResult =
  | { ok: true }
  | { ok: false; kind: 'conflict'; message: string }
  | { ok: false; kind: 'error'; message: string }

type ProjectionsState = {
  schema: ReturnType<typeof loadSchema>
  history: History | null
  model: ModelSummaryResponse | null
  cases: CaseListItem[]
  selectedCaseId: number | null
  selectedCase: CaseDetail | null
  saved: CaseInputs
  draft: CaseInputs
  dirty: boolean
  evaluated: Model | null
  view: ProjectionsView
  historyMonths: HistoryMonths
  loadError: string | null
  loading: boolean
  switchingCase: boolean
  saving: boolean
  importing: boolean
  importBlock: { control_status: string; detail: string } | null
  importDrift: { diff: unknown } | null
  sharedNotes: Record<string, string>
  notes: Record<string, string>
  lastExport: ProjectionExportListItem | null
  scheduleHealedNotice: {
    from: number | null
    to: number | null
    healed: boolean
    reason?: 'pref_accrual'
  } | null
  pendingMeta: { description?: string; expires_on?: string | null } | null
  pendingNotes: Record<string, string> | null
  editorName: string | null
  schemaStale: boolean
  compareMode: CompareMode
  compareColumns: CompareColumns
  snapshots: SnapshotRow[] | null
  snapshotsLoading: boolean
  compareModel: CompareModel | null

  load: () => Promise<void>
  selectCase: (id: number) => Promise<void>
  createCase: (name: string) => Promise<CreateCaseResult>
  duplicateCase: (name: string) => Promise<CreateCaseResult>
  updateCaseMeta: (meta: {
    name: string
    description: string | null
    expires_on: string | null
  }) => Promise<CaseMetaResult>
  deleteCase: () => Promise<void>
  setView: (view: ProjectionsView) => void
  setHistoryMonths: (months: HistoryMonths) => void
  setDriverType: (view: View, line: string, type: PickerDriverType) => void
  setDriverParams: (view: View, line: string, type: PickerDriver, params: DriverParams) => void
  setPctOfLineRef: (view: View, line: string, refKey: string) => void
  addOneTimeItem: (item: OneTimeItem) => void
  updateOneTimeItem: (id: string, patch: Partial<Omit<OneTimeItem, 'id'>>) => void
  removeOneTimeItem: (id: string) => void
  setEditorName: (name: string | null) => void
  setValue: (view: View, line: string, t: number, value: number | null) => void
  clearOverride: (view: View, line: string, t: number) => void
  setOverrideReason: (view: View, line: string, t: number, reason: string | null) => void
  applyFromColumn: (
    view: View,
    line: string,
    startT: number,
    mode?: 'keep_shape' | 'replace_flat',
  ) => void
  clearDriverValues: (view: View, line: string) => void
  clearDriver: (view: View, line: string) => void
  toggleConsolidatedOnly: (lineKey: string) => void
  discard: () => void
  setCashPolicy: (policy: CashPolicy | null) => void
  healStaleSchedule: () => Promise<void>
  reanchorRecipes: () => number
  holdPreRollValues: (view: View, lineKeys: string[]) => Promise<number>
  rollForward: (caseIds?: number[]) => Promise<RollForwardSummary>
  revertImport: () => Promise<{
    previous_import_id: number
    last_closed_month: string
    cases_restored: number
  }>
  refreshStaleSchedule: () => Promise<void>
  save: (acknowledgeWarnings: boolean) => Promise<SaveResult>
  importFromCgm: () => Promise<ImportResult>
  clearImportDrift: () => void
  reloadFromServer: () => Promise<void>
  adoptServerTimestamp: () => Promise<void>
  upsertNote: (cellKey: string, text: string) => Promise<{ ok: true } | { ok: false; message: string }>
  refreshExports: (caseId: number) => Promise<void>
  loadSnapshots: () => Promise<void>
  setCompareMode: (mode: CompareMode) => void
  toggleCompareColumn: (month: string) => void
  loadCaseFromFile: (
    parsed: ParsedCaseFile,
  ) => Promise<
    | { ok: true; hydrationProblems: string[] }
    | { ok: false; kind: 'conflict' | 'error'; message: string }
  >
}

function readHistoryMonths(): HistoryMonths {
  if (typeof localStorage === 'undefined') {
    return 12
  }
  const raw = localStorage.getItem(PROJECTIONS_HISTORY_MONTHS_KEY)
  if (raw === 'all') {
    return 'all'
  }
  if (raw === '3') {
    return 3
  }
  if (raw === '6') {
    return 6
  }
  if (raw === '12') {
    return 12
  }
  if (raw === '24') {
    return 24
  }
  return 12
}

function inputsEqual(a: CaseInputs, b: CaseInputs): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

function ensureLineDriver(inputs: CaseInputs, view: View, line: string) {
  if (!inputs.drivers[view]) {
    inputs.drivers[view] = {}
  }
  if (!inputs.drivers[view]![line]) {
    inputs.drivers[view]![line] = { active: null, by_type: {} }
  }
  return inputs.drivers[view]![line]!
}

function ensureDriverValues(values: (number | null)[] | undefined): (number | null)[] {
  if (!values || values.length !== 36) {
    return Array(36).fill(null)
  }
  return values
}

function parsePreRollValues(raw: (string | null)[]): (number | null)[] {
  const parsed: (number | null)[] = []
  for (let i = 0; i < 36; i++) {
    const cell = raw[i] ?? null
    if (cell == null) {
      parsed.push(null)
      continue
    }
    const n = parseNumeric(cell)
    parsed.push(typeof n === 'number' ? n : null)
  }
  return trailingNullFillPreRoll(parsed)
}

function trailingNullFillPreRoll(values: (number | null)[]): (number | null)[] {
  const result = values.slice(0, 36)
  while (result.length < 36) {
    result.push(null)
  }
  let anchor = -1
  for (let i = 35; i >= 0; i--) {
    if (result[i] != null) {
      anchor = i
      break
    }
  }
  if (anchor < 0) {
    return result
  }
  const fill = result[anchor]!
  for (let i = anchor + 1; i < 36; i++) {
    if (result[i] == null) {
      result[i] = fill
    }
  }
  return result
}

function cloneInputs(inputs: CaseInputs): CaseInputs {
  return structuredClone(inputs)
}

function formatSaveError(error: unknown): string {
  const detail = formatApiError(error, 'Failed to save case')
  if (
    error instanceof Error &&
    error.message === 'Network Error' &&
    detail === 'Network Error'
  ) {
    return 'Save failed: network error'
  }
  return detail
}

function pickDefaultCaseId(cases: CaseListItem[]): number | null {
  if (cases.length === 0) {
    return null
  }
  const nonExpired = cases.find((caseItem) => !caseItem.expired)
  return nonExpired?.id ?? cases[0]!.id
}

type MappedLoggedAfterCloseRow = {
  num: number
  date: string
  amount: number
  lines: Record<string, number> | null
}

function mapLoggedAfterClose(model: ModelSummaryResponse | null): MappedLoggedAfterCloseRow[] {
  return (model?.logged_after_close ?? []).map((row) => {
    const amountParsed = parseNumeric(row.amount)
    const amount = typeof amountParsed === 'number' ? amountParsed : 0
    const lines =
      row.lines == null
        ? null
        : Object.fromEntries(
            Object.entries(row.lines).map(([key, value]) => {
              const parsed = parseNumeric(value)
              return [key, typeof parsed === 'number' ? parsed : 0]
            }),
          )
    return {
      num: row.num,
      date: row.date,
      amount,
      lines,
    }
  })
}

function distributionContextFromModel(
  model: ModelSummaryResponse | null,
  policy: CashPolicy | null | undefined,
  loggedAfterCloseLive: MappedLoggedAfterCloseRow[],
): Pick<EvaluateContext, 'last_dist_num' | 'last_dist_date' | 'logged_after_close'> {
  const pinnedNum =
    policy?.enabled === true &&
    typeof policy.distribution.split_schedule?.basis.last_dist_num === 'number'
      ? policy.distribution.split_schedule.basis.last_dist_num
      : null

  if (pinnedNum == null) {
    return {
      last_dist_num: model?.last_dist_num ?? null,
      last_dist_date: model?.last_dist_date ?? null,
      logged_after_close: loggedAfterCloseLive,
    }
  }

  const modelMatchesPin = model?.last_dist_num === pinnedNum
  const logged_after_close = modelMatchesPin
    ? loggedAfterCloseLive
    : loggedAfterCloseLive.filter((row) => row.num <= pinnedNum)
  let last_dist_date: string | null
  if (modelMatchesPin) {
    last_dist_date = model?.last_dist_date ?? null
  } else if (logged_after_close.length > 0) {
    last_dist_date = logged_after_close.reduce(
      (max, row) => (row.date > max ? row.date : max),
      logged_after_close[0]!.date,
    )
  } else {
    last_dist_date = model?.latest_import?.last_closed_month ?? null
  }

  return {
    last_dist_num: pinnedNum,
    last_dist_date,
    logged_after_close,
  }
}

export function evaluateContextFromModel(
  model: ModelSummaryResponse | null,
  policy?: CashPolicy | null,
): EvaluateContext {
  const tier4Shares: Record<string, number> = {}
  if (model?.tier4_shares) {
    for (const [key, share] of Object.entries(model.tier4_shares)) {
      const parsed = parseNumeric(share)
      if (typeof parsed === 'number') {
        tier4Shares[key] = parsed
      }
    }
  }
  const loggedAfterCloseLive = mapLoggedAfterClose(model)
  const distribution = distributionContextFromModel(model, policy, loggedAfterCloseLive)
  const cashPolicySettings = (model?.settings?.cash_policy ?? {}) as Record<string, unknown>
  return {
    tier4_shares: tier4Shares,
    last_dist_num: distribution.last_dist_num,
    last_dist_date: distribution.last_dist_date,
    logged_after_close: distribution.logged_after_close,
    waterfall_mode: (cashPolicySettings.waterfall_mode as string | undefined) ?? null,
    last_closed_month: model?.latest_import?.last_closed_month ?? '',
  }
}

function evaluateContext(
  model: ModelSummaryResponse | null,
  policy?: CashPolicy | null,
): EvaluateContext {
  return evaluateContextFromModel(model, policy)
}

export function validationOptionsFromModel(
  model: ModelSummaryResponse | null,
  policy?: CashPolicy | null,
): ValidationOptions {
  const loggedAfterCloseLive = mapLoggedAfterClose(model)
  const distribution = distributionContextFromModel(model, policy, loggedAfterCloseLive)
  const cashPolicySettings = (model?.settings?.cash_policy ?? {}) as Record<string, unknown>
  return {
    lastDistNum: distribution.last_dist_num,
    lastDistDate: distribution.last_dist_date,
    lastClosedMonth: model?.latest_import?.last_closed_month ?? null,
    waterfallMode: (cashPolicySettings.waterfall_mode as string | undefined) ?? null,
  }
}

export function thresholdsFromSettings(model: ModelSummaryResponse | null): ValidationThresholds {
  const settings = model?.settings ?? {}
  const merged = { ...DEFAULT_THRESHOLDS }
  for (const key of Object.keys(DEFAULT_THRESHOLDS) as (keyof ValidationThresholds)[]) {
    const raw = settings[key]
    if (typeof raw === 'number' && Number.isFinite(raw)) {
      merged[key] = raw
    }
  }
  return merged
}

function recomputeEvaluated(
  schema: ReturnType<typeof loadSchema>,
  history: History | null,
  draft: CaseInputs,
  model: ModelSummaryResponse | null,
): Model | null {
  const lastClosedMonth = model?.latest_import?.last_closed_month
  if (!history || !lastClosedMonth) {
    return null
  }
  return evaluate(schema, history, draft, lastClosedMonth, evaluateContext(model, draft.cash_policy))
}

function recomputeCompareModel(
  snapshots: SnapshotRow[] | null,
  history: History | null,
  lastClosedMonth: string | undefined,
  schema: ReturnType<typeof loadSchema>,
): CompareModel | null {
  if (!snapshots || snapshots.length === 0 || !history || !lastClosedMonth) {
    return null
  }
  return buildCompareModel(snapshots, history, lastClosedMonth, schema)
}

function basisMatchesExceptCalendar(
  policy: NonNullable<CaseInputs['cash_policy']>,
  model: ModelSummaryResponse | null,
): boolean {
  const schedule = policy.distribution.split_schedule
  if (!schedule || !model) {
    return false
  }
  const basis = schedule.basis
  const lastDistNum = model.last_dist_num ?? null
  const lastClosedMonth = model.latest_import?.last_closed_month ?? ''
  const cashPolicySettings = (model.settings?.cash_policy ?? {}) as Record<string, unknown>
  const waterfallMode = (cashPolicySettings.waterfall_mode as string | undefined) ?? null
  const calendarMatches =
    basis.anchor_date === policy.distribution.anchor_date &&
    basis.cadence === policy.distribution.cadence &&
    Math.round(basis.amount_per_period * 100) ===
      Math.round((policy.distribution.amount_per_period ?? 0) * 100) &&
    (waterfallMode == null || basis.mode === waterfallMode)
  if (!calendarMatches) {
    return false
  }
  const distNumDiff = basis.last_dist_num !== (lastDistNum ?? null)
  const closedMonthDiff = basis.last_closed_month !== lastClosedMonth
  return (
    distNumDiff ||
    closedMonthDiff ||
    !Array.isArray(schedule.pref_accrual) ||
    !oneTimeBasisMatches(policy, model)
  )
}

function oneTimeBasisMatches(
  policy: NonNullable<CaseInputs['cash_policy']>,
  model: ModelSummaryResponse | null,
): boolean {
  const schedule = policy.distribution.split_schedule
  if (!schedule || !model) {
    return true
  }
  const basis = schedule.basis
  const lastClosedMonth = model.latest_import?.last_closed_month ?? ''
  const policyExtra = effectiveOneTime(
    policy,
    model.last_dist_date ?? null,
    lastClosedMonth,
  )
  const basisExtraDate = basis.one_time_date ?? null
  const basisExtraAmount = basis.one_time_amount ?? null
  if (policyExtra) {
    if (basisExtraDate !== policyExtra.date) {
      return false
    }
    if (
      basisExtraAmount == null ||
      Math.round(basisExtraAmount * 100) !== Math.round(policyExtra.amount * 100)
    ) {
      return false
    }
  } else if (
    basisExtraDate != null ||
    (basisExtraAmount != null && basisExtraAmount !== 0)
  ) {
    return false
  }
  return true
}

function isScheduleStale(
  policy: NonNullable<CaseInputs['cash_policy']>,
  model: ModelSummaryResponse | null,
): boolean {
  const schedule = policy.distribution.split_schedule
  if (!schedule || !model) {
    return true
  }
  const basis = schedule.basis
  const lastDistNum = model.last_dist_num ?? null
  const lastClosedMonth = model.latest_import?.last_closed_month ?? ''
  const cashPolicySettings = (model.settings?.cash_policy ?? {}) as Record<string, unknown>
  const waterfallMode = (cashPolicySettings.waterfall_mode as string | undefined) ?? null
  return (
    basis.anchor_date !== policy.distribution.anchor_date ||
    basis.cadence !== policy.distribution.cadence ||
    Math.round(basis.amount_per_period * 100) !==
      Math.round((policy.distribution.amount_per_period ?? 0) * 100) ||
    basis.last_dist_num !== (lastDistNum ?? null) ||
    basis.last_closed_month !== lastClosedMonth ||
    (waterfallMode != null && basis.mode !== waterfallMode) ||
    !Array.isArray(schedule.pref_accrual) ||
    !oneTimeBasisMatches(policy, model)
  )
}

function isPrefAccrualOnlyStale(
  policy: NonNullable<CaseInputs['cash_policy']>,
  model: ModelSummaryResponse,
): boolean {
  const schedule = policy.distribution.split_schedule
  if (!schedule) {
    return false
  }
  if (Array.isArray(schedule.pref_accrual)) {
    return false
  }
  const basis = schedule.basis
  const lastDistNum = model.last_dist_num ?? null
  const lastClosedMonth = model.latest_import?.last_closed_month ?? ''
  const cashPolicySettings = (model.settings?.cash_policy ?? {}) as Record<string, unknown>
  const waterfallMode = (cashPolicySettings.waterfall_mode as string | undefined) ?? null
  const calendarMatches =
    basis.anchor_date === policy.distribution.anchor_date &&
    basis.cadence === policy.distribution.cadence &&
    Math.round(basis.amount_per_period * 100) ===
      Math.round((policy.distribution.amount_per_period ?? 0) * 100) &&
    (waterfallMode == null || basis.mode === waterfallMode)
  return (
    calendarMatches &&
    basis.last_dist_num === lastDistNum &&
    basis.last_closed_month === lastClosedMonth
  )
}

function isDirty(
  state: Pick<ProjectionsState, 'saved' | 'pendingMeta' | 'pendingNotes'>,
  draft: CaseInputs,
): boolean {
  return (
    !inputsEqual(draft, state.saved) ||
    state.pendingMeta != null ||
    state.pendingNotes != null
  )
}

function replaceDraftInputs(draft: CaseInputs, next: CaseInputs): void {
  draft.consolidated_only = next.consolidated_only
  draft.drivers = next.drivers
  draft.one_time_items = next.one_time_items
  draft.cash_policy = next.cash_policy
}

function applyDraftMutation(
  state: ProjectionsState,
  mutate: (draft: CaseInputs) => void,
): Partial<ProjectionsState> {
  const draft = cloneInputs(state.draft)
  mutate(draft)
  const dirty = isDirty(state, draft)
  const evaluated = recomputeEvaluated(state.schema, state.history, draft, state.model)
  return { draft, dirty, evaluated }
}

export const initialProjectionsState = {
  schema: loadSchema(),
  history: null,
  model: null,
  cases: [],
  selectedCaseId: null,
  selectedCase: null,
  saved: emptyInputs(),
  draft: emptyInputs(),
  dirty: false,
  evaluated: null,
  view: 'river_oaks' as ProjectionsView,
  historyMonths: readHistoryMonths(),
  loadError: null,
  loading: false,
  switchingCase: false,
  saving: false,
  importing: false,
  importBlock: null,
  importDrift: null,
  sharedNotes: {},
  notes: {},
  lastExport: null,
  scheduleHealedNotice: null,
  pendingMeta: null,
  pendingNotes: null,
  editorName: null,
  schemaStale: false,
  compareMode: readCompareMode(),
  compareColumns: 'all' as CompareColumns,
  snapshots: null,
  snapshotsLoading: false,
  compareModel: null,
}

function rematerialise(
  draft: CaseInputs,
  view: View,
  lineKey: string,
  state: Pick<ProjectionsState, 'model' | 'history' | 'schema'>,
): void {
  const lineState = draft.drivers[view]?.[lineKey]
  const active = lineState?.active as DriverType | null | undefined
  if (!active || !lineState) {
    return
  }
  const cache = lineState.by_type[active]
  const recipe = cache?.recipe
  if (!recipe) {
    return
  }
  const lastClosedMonth = state.model?.latest_import?.last_closed_month ?? ''
  const projectionMonths = projectionMonthKeys(lastClosedMonth)
  let nextRecipe = recipe
  if (state.history && recipe.anchor.basis && recipe.anchor.basis !== 'manual') {
    const historyValue = anchorFromHistory(
      recipe.anchor.basis,
      recipe.anchor.basis === 'trailing_avg_per_period'
        ? (recipe.anchor.runs ?? 3)
        : (recipe.anchor.months ?? 3),
      {
        view,
        lineKey,
        driverType: active,
        params: cache?.params ?? {},
        history: state.history,
        schema: state.schema,
        lastClosedMonth,
      },
    )
    if (historyValue != null) {
      nextRecipe = {
        ...recipe,
        anchor: { ...recipe.anchor, history_value: historyValue },
      }
      cache!.recipe = nextRecipe
    }
  }
  const seasonalityResult = resolveSeasonalityFactors(nextRecipe, {
    view,
    lineKey,
    driverType: active,
    params: cache?.params ?? {},
    history: state.history ?? undefined,
    schema: state.schema,
    lastClosedMonth,
    pathPrefix: `drivers.${view}.${lineKey}.recipe`,
  })
  cache!.values = materialise(nextRecipe, {
    lastClosedMonth,
    driverType: active,
    unit: driverInputUnit(active as Parameters<typeof driverInputUnit>[0]),
    seasonalityFactors: seasonalityResult.factors,
    projectionMonths,
  })
}

function rematerialiseAllRecipes(
  draft: CaseInputs,
  state: Pick<ProjectionsState, 'model' | 'history' | 'schema'>,
): void {
  for (const view of Object.keys(draft.drivers) as View[]) {
    const lines = draft.drivers[view]
    if (!lines) {
      continue
    }
    for (const lineKey of Object.keys(lines)) {
      rematerialise(draft, view, lineKey, state)
    }
  }
}

export function recipeLineCount(draft: CaseInputs): number {
  let count = 0
  for (const view of Object.keys(draft.drivers) as View[]) {
    const lines = draft.drivers[view]
    if (!lines) {
      continue
    }
    for (const lineKey of Object.keys(lines)) {
      const lineState = lines[lineKey]
      const active = lineState?.active
      if (!active || !lineState) {
        continue
      }
      const cache = lineState.by_type[active]
      if (cache?.recipe) {
        count += 1
      }
    }
  }
  return count
}

function mergeNotes(
  sharedNotes: Record<string, string>,
  caseNotes: Record<string, string>,
): Record<string, string> {
  return { ...sharedNotes, ...caseNotes }
}

async function loadSharedNotes(): Promise<Record<string, string>> {
  try {
    return await getSharedNotes()
  } catch {
    return {}
  }
}

async function loadNotesForCase(caseId: number): Promise<Record<string, string>> {
  try {
    return await getNotes(caseId)
  } catch {
    return {}
  }
}

const NOTE_WRITE_CONCURRENCY = 4

async function writePendingNotesInBackground(
  caseId: number,
  notesSnapshot: Record<string, string>,
  getSharedNotes: () => Record<string, string>,
  applyNotes: (notes: Record<string, string>) => void,
): Promise<void> {
  const entries = Object.entries(notesSnapshot)
  const total = entries.length
  if (total === 0) return

  let completed = 0
  let noteFailures = 0
  let progressToastId: string | null = showToast({
    variant: 'warning',
    message: `Saving notes… 0/${total}`,
  })
  let nextIndex = 0

  const updateProgress = () => {
    if (progressToastId) dismissToast(progressToastId)
    if (completed < total) {
      progressToastId = showToast({
        variant: 'warning',
        message: `Saving notes… ${completed}/${total}`,
      })
    } else {
      progressToastId = null
    }
  }

  async function worker(): Promise<void> {
    while (nextIndex < entries.length) {
      const index = nextIndex
      nextIndex += 1
      const entry = entries[index]
      if (!entry) continue
      const [cellKey, text] = entry
      try {
        await upsertNoteApi(caseId, cellKey, text)
      } catch {
        noteFailures += 1
      }
      completed += 1
      updateProgress()
    }
  }

  const workerCount = Math.min(NOTE_WRITE_CONCURRENCY, total)
  await Promise.all(Array.from({ length: workerCount }, () => worker()))

  if (progressToastId) {
    dismissToast(progressToastId)
  }

  const caseNotes = await loadNotesForCase(caseId)
  applyNotes(mergeNotes(getSharedNotes(), caseNotes))

  if (noteFailures > 0) {
    toast.error(
      `Case saved, but ${noteFailures} note${noteFailures === 1 ? '' : 's'} could not be written.`,
    )
  }
}

async function loadLastExportForCase(caseId: number): Promise<ProjectionExportListItem | null> {
  try {
    const rows = await listExports(caseId)
    return rows[0] ?? null
  } catch {
    return null
  }
}

export const useProjectionsStore = create<ProjectionsState>((set, get) => ({
  ...initialProjectionsState,

  load: async () => {
    const prev = get()
    const preserveDraft =
      prev.dirty && prev.selectedCaseId != null ? cloneInputs(prev.draft) : null
    const preserveCaseId = prev.selectedCaseId

    set({ loading: true, loadError: null })
    try {
      const [model, cases] = await Promise.all([getModel(), listCases()])
      let history: History | null = null
      let schemaStale = false
      try {
        const payload = await getHistory()
        schemaStale = payload.schema_stale === true
        history = historyFromApiPayload(
          payload as ApiHistoryPayload,
          model.latest_import?.last_closed_month ?? '',
        )
      } catch (error) {
        const status =
          error != null &&
          typeof error === 'object' &&
          'response' in error &&
          (error as { response?: { status?: number } }).response?.status
        if (status !== 404) {
          throw error
        }
      }

      const selectedCaseId =
        preserveCaseId != null && cases.some((caseItem) => caseItem.id === preserveCaseId)
          ? preserveCaseId
          : pickDefaultCaseId(cases)
      let selectedCase: CaseDetail | null = null
      let saved = emptyInputs()
      let draft = emptyInputs()
      let evaluated: Model | null = null
      let dirty = false

      const sharedNotes = await loadSharedNotes()
      let notes: Record<string, string> = sharedNotes
      let lastExport: ProjectionExportListItem | null = null
      if (selectedCaseId != null) {
        selectedCase = await getCase(selectedCaseId)
        saved = toEngineInputs(selectedCase.inputs)
        if (preserveDraft && selectedCaseId === preserveCaseId) {
          draft = preserveDraft
          dirty = isDirty(prev, draft)
        } else {
          draft = cloneInputs(saved)
        }
        evaluated = recomputeEvaluated(initialProjectionsState.schema, history, draft, model)
        const [caseNotes, exportRow] = await Promise.all([
          loadNotesForCase(selectedCaseId),
          loadLastExportForCase(selectedCaseId),
        ])
        notes = mergeNotes(sharedNotes, caseNotes)
        lastExport = exportRow
      }

      writePersistedSelectedCaseId(selectedCaseId)
      set({
        model,
        cases,
        history,
        schemaStale,
        selectedCaseId,
        selectedCase,
        saved,
        draft,
        dirty,
        evaluated,
        sharedNotes,
        notes,
        lastExport,
        scheduleHealedNotice: null,
        pendingMeta: preserveCaseId === selectedCaseId ? prev.pendingMeta : null,
        pendingNotes: preserveCaseId === selectedCaseId ? prev.pendingNotes : null,
        loading: false,
        loadError: null,
        snapshots: null,
        compareModel: null,
        snapshotsLoading: false,
      })
      if (get().compareMode === 'on' && get().selectedCaseId != null) {
        void get().loadSnapshots()
      }
    } catch (error) {
      set({
        loading: false,
        loadError: formatApiError(error, 'Failed to load projections'),
      })
    }
  },

  selectCase: async (id) => {
    set({ switchingCase: true, loadError: null })
    try {
      const [selectedCase, caseNotes, lastExport] = await Promise.all([
        getCase(id),
        loadNotesForCase(id),
        loadLastExportForCase(id),
      ])
      const saved = toEngineInputs(selectedCase.inputs)
      const draft = cloneInputs(saved)
      const state = get()
      const evaluated = recomputeEvaluated(state.schema, state.history, draft, state.model)
      writePersistedSelectedCaseId(id)
      set({
        selectedCaseId: id,
        selectedCase,
        saved,
        draft,
        dirty: false,
        evaluated,
        notes: mergeNotes(state.sharedNotes, caseNotes),
        lastExport,
        scheduleHealedNotice: null,
        pendingMeta: null,
        pendingNotes: null,
        switchingCase: false,
        snapshots: null,
        compareModel: null,
        snapshotsLoading: false,
      })
      if (get().compareMode === 'on' && get().selectedCaseId != null) {
        void get().loadSnapshots()
      }
    } catch (error) {
      set({
        switchingCase: false,
        loadError: formatApiError(error, 'Failed to load case'),
      })
    }
  },

  createCase: async (name) => {
    set({ saving: true, loadError: null })
    try {
      const selectedCase = await createCase({ name })
      const [caseNotes, lastExport, cases] = await Promise.all([
        loadNotesForCase(selectedCase.id),
        loadLastExportForCase(selectedCase.id),
        listCases(),
      ])
      const saved = toEngineInputs(selectedCase.inputs)
      const draft = cloneInputs(saved)
      const state = get()
      const evaluated = recomputeEvaluated(state.schema, state.history, draft, state.model)
      writePersistedSelectedCaseId(selectedCase.id)
      set({
        cases,
        selectedCaseId: selectedCase.id,
        selectedCase,
        saved,
        draft,
        dirty: false,
        evaluated,
        notes: mergeNotes(state.sharedNotes, caseNotes),
        lastExport,
        pendingMeta: null,
        pendingNotes: null,
        saving: false,
        snapshots: null,
        compareModel: null,
        snapshotsLoading: false,
      })
      if (get().compareMode === 'on' && get().selectedCaseId != null) {
        void get().loadSnapshots()
      }
      return { ok: true }
    } catch (error) {
      set({ saving: false })
      if (
        error != null &&
        typeof error === 'object' &&
        'response' in error &&
        (error as { response?: { status?: number } }).response?.status === 409
      ) {
        return {
          ok: false,
          kind: 'conflict',
          message: formatApiError(error, 'Failed to create case'),
        }
      }
      set({ loadError: formatApiError(error, 'Failed to create case') })
      return { ok: false, kind: 'error', message: formatApiError(error, 'Failed to create case') }
    }
  },

  duplicateCase: async (name) => {
    const { selectedCase } = get()
    if (!selectedCase) {
      return { ok: false, kind: 'error', message: 'No case selected' }
    }
    set({ saving: true, loadError: null })
    try {
      const created = await createCase({ name, from_case_id: selectedCase.id })
      const [caseNotes, lastExport, cases] = await Promise.all([
        loadNotesForCase(created.id),
        loadLastExportForCase(created.id),
        listCases(),
      ])
      const saved = toEngineInputs(created.inputs)
      const draft = cloneInputs(saved)
      const state = get()
      const evaluated = recomputeEvaluated(state.schema, state.history, draft, state.model)
      writePersistedSelectedCaseId(created.id)
      set({
        cases,
        selectedCaseId: created.id,
        selectedCase: created,
        saved,
        draft,
        dirty: false,
        evaluated,
        notes: mergeNotes(state.sharedNotes, caseNotes),
        lastExport,
        pendingMeta: null,
        pendingNotes: null,
        saving: false,
        snapshots: null,
        compareModel: null,
        snapshotsLoading: false,
      })
      if (get().compareMode === 'on' && get().selectedCaseId != null) {
        void get().loadSnapshots()
      }
      return { ok: true }
    } catch (error) {
      set({ saving: false })
      if (
        error != null &&
        typeof error === 'object' &&
        'response' in error &&
        (error as { response?: { status?: number } }).response?.status === 409
      ) {
        return {
          ok: false,
          kind: 'conflict',
          message: formatApiError(error, 'Failed to duplicate case'),
        }
      }
      set({ loadError: formatApiError(error, 'Failed to duplicate case') })
      return { ok: false, kind: 'error', message: formatApiError(error, 'Failed to duplicate case') }
    }
  },

  updateCaseMeta: async (meta) => {
    const { selectedCase, saved, schema, history, model } = get()
    if (!selectedCase) {
      return { ok: false, kind: 'error', message: 'No case selected' }
    }
    const evaluatedFromSaved = recomputeEvaluated(schema, history, saved, model)
    if (!evaluatedFromSaved) {
      return { ok: false, kind: 'error', message: 'Model data is not available' }
    }
    set({ saving: true, loadError: null })
    try {
      const updated = await updateCase(selectedCase.id, {
        name: meta.name,
        description: meta.description,
        expires_on: meta.expires_on,
        inputs: toApiInputs(saved),
        outputs: toApiOutputs(evaluatedFromSaved),
        kpis: toApiKpis(evaluatedFromSaved),
        expected_updated_at: selectedCase.updated_at,
        acknowledge_warnings: true,
      })
      const nextSaved = toEngineInputs(updated.inputs)
      const cases = await listCases()
      const state = get()
      const evaluated = recomputeEvaluated(schema, history, state.draft, model)
      set({
        selectedCase: updated,
        cases,
        saved: nextSaved,
        dirty: isDirty({ ...state, pendingMeta: null, pendingNotes: null }, state.draft),
        evaluated,
        saving: false,
      })
      return { ok: true, case: updated }
    } catch (error) {
      const concurrency = isConcurrencyConflict(error)
      if (concurrency) {
        set({ saving: false })
        return { ok: false, kind: 'concurrency', case: concurrency.case }
      }
      set({
        saving: false,
        loadError: formatApiError(error, 'Failed to update case'),
      })
      return { ok: false, kind: 'error', message: formatApiError(error, 'Failed to update case') }
    }
  },

  deleteCase: async () => {
    const { selectedCaseId, cases } = get()
    if (selectedCaseId == null) {
      return
    }
    set({ saving: true, loadError: null })
    try {
      await deleteCaseApi(selectedCaseId)
      const remaining = cases.filter((caseItem) => caseItem.id !== selectedCaseId)
      const nextId = pickDefaultCaseId(remaining)
      if (nextId == null) {
        writePersistedSelectedCaseId(null)
        set({
          cases: remaining,
          selectedCaseId: null,
          selectedCase: null,
          saved: emptyInputs(),
          draft: emptyInputs(),
          dirty: false,
          evaluated: null,
          notes: get().sharedNotes,
          lastExport: null,
          pendingMeta: null,
          pendingNotes: null,
          saving: false,
        })
        return
      }
      const [selectedCase, caseNotes, lastExport] = await Promise.all([
        getCase(nextId),
        loadNotesForCase(nextId),
        loadLastExportForCase(nextId),
      ])
      const saved = toEngineInputs(selectedCase.inputs)
      const draft = cloneInputs(saved)
      const state = get()
      const evaluated = recomputeEvaluated(state.schema, state.history, draft, state.model)
      writePersistedSelectedCaseId(nextId)
      set({
        cases: remaining,
        selectedCaseId: nextId,
        selectedCase,
        saved,
        draft,
        dirty: false,
        evaluated,
        notes: mergeNotes(state.sharedNotes, caseNotes),
        lastExport,
        pendingMeta: null,
        pendingNotes: null,
        saving: false,
      })
    } catch (error) {
      set({
        saving: false,
        loadError: formatApiError(error, 'Failed to delete case'),
      })
    }
  },

  setView: (view) => set({ view }),

  setHistoryMonths: (months) => {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(PROJECTIONS_HISTORY_MONTHS_KEY, String(months))
    }
    set({ historyMonths: months })
  },

  loadSnapshots: async () => {
    const { selectedCaseId, schema, history, model } = get()
    if (selectedCaseId == null) {
      return
    }
    set({ snapshotsLoading: true })
    try {
      const snapshots = await listCaseSnapshots(selectedCaseId, true)
      set({
        snapshots,
        snapshotsLoading: false,
        compareModel: recomputeCompareModel(
          snapshots,
          history,
          model?.latest_import?.last_closed_month,
          schema,
        ),
      })
    } catch {
      set({ snapshotsLoading: false })
    }
  },

  setCompareMode: (mode) => {
    writeCompareMode(mode)
    set({ compareMode: mode })
    if (mode === 'on' && get().snapshots == null && get().selectedCaseId != null) {
      void get().loadSnapshots()
    }
  },

  toggleCompareColumn: (month) => {
    const { compareColumns, compareModel, snapshots } = get()
    const allMonths = compareModel?.months ?? snapshots?.map((s) => s.month) ?? []
    if (compareColumns === 'all') {
      set({ compareColumns: allMonths.filter((m) => m !== month) })
      return
    }
    if (compareColumns.includes(month)) {
      set({ compareColumns: compareColumns.filter((m) => m !== month) })
    } else {
      set({ compareColumns: [...compareColumns, month] })
    }
  },

  setDriverType: (view, line, type) => {
    set((state) =>
      applyDraftMutation(state, (draft) => {
        const memberGroup = ignoreGroupForLine(state.schema, line)

        if (type === 'ignore' && memberGroup) {
          for (const member of memberGroup.members) {
            ensureLineDriver(draft, view, member).active = 'ignore'
          }
          return
        }

        if (
          memberGroup &&
          isGroupIgnored(draft, view, memberGroup) &&
          type !== 'ignore'
        ) {
          for (const member of memberGroup.members) {
            const memberState = ensureLineDriver(draft, view, member)
            if (member === line) {
              memberState.active = type
              if (type !== null) {
                if (!memberState.by_type[type]) {
                  memberState.by_type[type] = { values: Array(36).fill(null) }
                } else {
                  memberState.by_type[type]!.values = ensureDriverValues(
                    memberState.by_type[type]!.values,
                  )
                }
              }
            } else {
              memberState.active = null
            }
          }
          ensureLineDriver(draft, view, memberGroup.parent).active = null
          return
        }

        const lineState = ensureLineDriver(draft, view, line)
        lineState.active = type
        if (type !== null && type !== 'ignore') {
          if (!lineState.by_type[type]) {
            lineState.by_type[type] = { values: Array(36).fill(null) }
          } else {
            lineState.by_type[type]!.values = ensureDriverValues(lineState.by_type[type]!.values)
          }
        }
        if (type === 'labor_buildup' && line === 'is.payroll_wages' && view !== 'consolidated') {
          const existing = lineState.by_type.labor_buildup
          const cache: DriverTypeInput = existing ?? { values: Array(36).fill(null), params: {} }
          const params: DriverParams = { ...(cache.params ?? {}) }
          const anchor =
            params.anchor_date ?? draft.cash_policy?.distribution?.anchor_date ?? undefined
          if (anchor && !params.anchor_date) {
            params.anchor_date = anchor
          }
          if (!params.cadence) {
            params.cadence = 'biweekly'
          }
          if (params.net_of_benefits_pct == null) {
            params.net_of_benefits_pct = 0
          }
          lineState.by_type.labor_buildup = { ...cache, params }
          for (const derivedKey of ['is.boh', 'is.foh'] as const) {
            const derived = ensureLineDriver(draft, view, derivedKey)
            derived.active = null
          }
        }
      }),
    )
  },

  setDriverParams: (view, line, type, params) => {
    set((state) =>
      applyDraftMutation(state, (draft) => {
        const lineState = ensureLineDriver(draft, view, line)
        const existing: DriverTypeInput = lineState.by_type[type] ?? { values: Array(36).fill(null) }
        const mergedParams = { ...(existing.params ?? {}), ...params }
        if (type === 's_curve') {
          const start = mergedParams.start ?? 0
          const end = mergedParams.end ?? 0
          const midpoint = mergedParams.midpoint ?? 18
          const steepness = mergedParams.steepness ?? 2
          lineState.by_type[type] = {
            params: { start, end, midpoint, steepness },
            values: materialize({ start, end, midpoint, steepness }),
          }
        } else {
          lineState.by_type[type] = { ...existing, params: mergedParams }
        }
        lineState.active = type
      }),
    )
  },

  addOneTimeItem: (item) => {
    set((state) =>
      applyDraftMutation(state, (draft) => {
        draft.one_time_items.push(item)
      }),
    )
  },

  updateOneTimeItem: (id, patch) => {
    set((state) =>
      applyDraftMutation(state, (draft) => {
        const index = draft.one_time_items.findIndex((item) => item.id === id)
        if (index < 0) {
          return
        }
        draft.one_time_items[index] = { ...draft.one_time_items[index]!, ...patch }
      }),
    )
  },

  removeOneTimeItem: (id) => {
    set((state) =>
      applyDraftMutation(state, (draft) => {
        draft.one_time_items = draft.one_time_items.filter((item) => item.id !== id)
      }),
    )
  },

  setPctOfLineRef: (view, line, refKey) => {
    set((state) =>
      applyDraftMutation(state, (draft) => {
        const lineState = ensureLineDriver(draft, view, line)
        const active = lineState.active
        const driverType =
          active === 'dollar_per_line_unit' ? 'dollar_per_line_unit' : 'pct_of_line'
        if (!lineState.by_type[driverType]) {
          lineState.by_type[driverType] = { values: Array(36).fill(null), params: { ref: refKey } }
        } else {
          lineState.by_type[driverType]!.params = {
            ...lineState.by_type[driverType]!.params,
            ref: refKey,
          }
        }
      }),
    )
  },

  setEditorName: (name) => set({ editorName: name }),

  setValue: (view, line, t, value) => {
    if (t < 1 || t > 36) {
      return
    }
    set((state) =>
      applyDraftMutation(state, (draft) => {
        const lineState = draft.drivers[view]?.[line]
        const active = lineState?.active
        if (!active) {
          return
        }
        const cache = lineState.by_type[active]
        if (!cache) {
          return
        }
        const recipe = cache.recipe
        if (recipe && value === null) {
          const lastClosed = state.model?.latest_import?.last_closed_month ?? ''
          const monthKey = monthKeyFromT(lastClosed, t)
          if (recipe.overrides?.[monthKey] !== undefined) {
            const nextOverrides = { ...recipe.overrides }
            delete nextOverrides[monthKey]
            cache.recipe = {
              ...recipe,
              overrides: Object.keys(nextOverrides).length > 0 ? nextOverrides : undefined,
            }
            rematerialise(draft, view, line, state)
          }
          return
        }
        if (recipe && typeof value === 'number') {
          const lastClosed = state.model?.latest_import?.last_closed_month ?? ''
          const monthKey = monthKeyFromT(lastClosed, t)
          const editor = state.editorName || 'unknown'
          const nextOverrides = { ...(recipe.overrides ?? {}) }
          nextOverrides[monthKey] = {
            value,
            reason: null,
            edited_by: editor,
            edited_at: new Date().toISOString(),
          }
          cache.recipe = { ...recipe, overrides: nextOverrides }
          rematerialise(draft, view, line, state)
          return
        }
        const values = ensureDriverValues(cache.values)
        values[t - 1] = value
        cache.values = values
      }),
    )
  },

  clearOverride: (view, line, t) => {
    if (t < 1 || t > 36) {
      return
    }
    set((state) =>
      applyDraftMutation(state, (draft) => {
        const lineState = draft.drivers[view]?.[line]
        const active = lineState?.active
        if (!active) {
          return
        }
        const cache = lineState.by_type[active]
        const recipe = cache?.recipe
        if (!recipe?.overrides) {
          return
        }
        const lastClosed = state.model?.latest_import?.last_closed_month ?? ''
        const monthKey = monthKeyFromT(lastClosed, t)
        if (recipe.overrides[monthKey] === undefined) {
          return
        }
        const nextOverrides = { ...recipe.overrides }
        delete nextOverrides[monthKey]
        cache!.recipe = {
          ...recipe,
          overrides: Object.keys(nextOverrides).length > 0 ? nextOverrides : undefined,
        }
        rematerialise(draft, view, line, state)
      }),
    )
  },

  setOverrideReason: (view, line, t, reason) => {
    if (t < 1 || t > 36) {
      return
    }
    set((state) =>
      applyDraftMutation(state, (draft) => {
        const lineState = draft.drivers[view]?.[line]
        const active = lineState?.active
        if (!active) {
          return
        }
        const cache = lineState.by_type[active]
        const recipe = cache?.recipe
        if (!recipe?.overrides) {
          return
        }
        const lastClosed = state.model?.latest_import?.last_closed_month ?? ''
        const monthKey = monthKeyFromT(lastClosed, t)
        const existing = recipe.overrides[monthKey]
        if (existing === undefined) {
          return
        }
        const ov = typeof existing === 'number' ? { value: existing } : { ...existing }
        cache!.recipe = {
          ...recipe,
          overrides: {
            ...recipe.overrides,
            [monthKey]: { ...ov, reason },
          },
        }
      }),
    )
  },

  applyFromColumn: (view, line, startT, mode) => {
    if (startT < 2 || startT > 36) {
      return
    }
    set((state) =>
      applyDraftMutation(state, (draft) => {
        const lineState = draft.drivers[view]?.[line]
        const active = lineState?.active
        if (!active) {
          return
        }
        const cache = lineState.by_type[active]
        if (!cache) {
          return
        }
        const recipe = cache.recipe
        if (recipe && !mode) {
          return
        }
        if (recipe && mode === 'keep_shape') {
          const lastClosed = state.model?.latest_import?.last_closed_month ?? ''
          const values = ensureDriverValues(cache.values)
          const typed = startT >= 2 ? values[startT - 2] : values[startT - 1]
          if (typed == null) {
            return
          }
          const matchT = startT >= 2 ? startT - 1 : startT
          const projectionMonths = projectionMonthKeys(lastClosed)
          const seasonalityResult = resolveSeasonalityFactors(recipe, {
            view,
            lineKey: line,
            driverType: active as DriverType,
            params: cache.params ?? {},
            history: state.history ?? undefined,
            schema: state.schema,
            lastClosedMonth: lastClosed,
            pathPrefix: `drivers.${view}.${line}.recipe`,
          })
          const ctx = {
            lastClosedMonth: lastClosed,
            driverType: active as DriverType,
            unit: driverInputUnit(active as Parameters<typeof driverInputUnit>[0]),
            seasonalityFactors: seasonalityResult.factors,
            projectionMonths,
          }
          const newAnchor = solveAnchorValue(recipe, matchT, typed, ctx)
          const monthKey = monthKeyFromT(lastClosed, matchT)
          const nextOverrides = recipe.overrides ? { ...recipe.overrides } : undefined
          if (nextOverrides?.[monthKey] !== undefined) {
            delete nextOverrides[monthKey]
          }
          cache.recipe = {
            ...recipe,
            anchor: { ...recipe.anchor, value: newAnchor, basis: 'manual' },
            overrides:
              nextOverrides && Object.keys(nextOverrides).length > 0
                ? nextOverrides
                : undefined,
          }
          rematerialise(draft, view, line, state)
          return
        }
        if (recipe && mode === 'replace_flat') {
          cache.recipe = null
        }
        const values = ensureDriverValues(cache.values)
        const source = values[startT - 2] ?? null
        for (let i = startT - 1; i < 36; i += 1) {
          values[i] = source
        }
        cache.values = values
      }),
    )
  },

  clearDriverValues: (view, line) => {
    set((state) =>
      applyDraftMutation(state, (draft) => {
        const lineState = draft.drivers[view]?.[line]
        const active = lineState?.active
        if (!active) {
          return
        }
        const cache = lineState.by_type[active]
        if (!cache) {
          return
        }
        cache.values = Array(36).fill(null)
        cache.recipe = null
      }),
    )
  },

  clearDriver: (view, line) => {
    set((state) =>
      applyDraftMutation(state, (draft) => {
        const memberGroup = ignoreGroupForLine(state.schema, line)
        if (memberGroup && isGroupIgnored(draft, view, memberGroup)) {
          for (const member of memberGroup.members) {
            const memberState = ensureLineDriver(draft, view, member)
            if (member === line) {
              memberState.active = null
            } else {
              memberState.active = null
            }
          }
          ensureLineDriver(draft, view, memberGroup.parent).active = null
          return
        }

        const lineState = ensureLineDriver(draft, view, line)
        lineState.active = null
        for (const type of Object.keys(lineState.by_type)) {
          lineState.by_type[type]!.values = Array(36).fill(null)
        }
      }),
    )
  },

  toggleConsolidatedOnly: (lineKey) => {
    set((state) =>
      applyDraftMutation(state, (draft) => {
        const index = draft.consolidated_only.indexOf(lineKey)
        if (index >= 0) {
          draft.consolidated_only.splice(index, 1)
        } else {
          draft.consolidated_only.push(lineKey)
        }
      }),
    )
  },

  discard: () => {
    const state = get()
    const draft = cloneInputs(state.saved)
    const evaluated = recomputeEvaluated(state.schema, state.history, draft, state.model)
    set({
      draft,
      dirty: false,
      evaluated,
      scheduleHealedNotice: null,
      pendingMeta: null,
      pendingNotes: null,
    })
  },

  setCashPolicy: (policy) => {
    set((state) =>
      applyDraftMutation(state, (draft) => {
        draft.cash_policy = policy
      }),
    )
  },

  reanchorRecipes: () => {
    const state = get()
    const count = recipeLineCount(state.draft)
    set(applyDraftMutation(state, (draft) => rematerialiseAllRecipes(draft, get())))
    return count
  },

  holdPreRollValues: async (view, lineKeys) => {
    const state = get()
    const preRoll = state.selectedCase?.pre_rollforward_outputs
    if (!preRoll) {
      return 0
    }
    const lastClosedMonth = state.model?.latest_import?.last_closed_month ?? ''
    const rollForwardMonth = formatMonthLabel(monthLabel(lastClosedMonth, 0))
    const heldOn = todayIsoDate()
    const noteUpdates: { cellKey: string; text: string }[] = []
    let applied = 0

    set(
      applyDraftMutation(state, (draft) => {
        for (const lineKey of lineKeys) {
          const raw = preRoll[view]?.[lineKey]
          if (!raw) {
            continue
          }
          const entry = state.schema.find((row) => row.key === lineKey)
          if (!entry) {
            continue
          }
          const values = parsePreRollValues(raw)
          const driverType: DriverType = entry.unit === 'count' ? 'count_value' : 'dollar_value'
          const lineState = ensureLineDriver(draft, view, lineKey)
          lineState.active = driverType
          const existingCache = lineState.by_type[driverType]
          lineState.by_type[driverType] = {
            ...existingCache,
            values: values.slice(),
          }
          applied++
          const cellKey = methodologyCellKey(view, lineKey)
          const prior = state.notes[cellKey] ?? ''
          const sentence = `Held pre-roll projected values on ${heldOn} (roll-forward to ${rollForwardMonth})`
          const text = prior.trim() ? `${prior.trim()}\n${sentence}` : sentence
          noteUpdates.push({ cellKey, text })
        }
      }),
    )

    for (const { cellKey, text } of noteUpdates) {
      await get().upsertNote(cellKey, text)
    }
    return applied
  },

  rollForward: async (caseIds?: number[]) => {
    const summary = await rollForwardCases(caseIds)
    await get().load()
    return summary
  },

  revertImport: async () => {
    const result = await revertLatestImport()
    await get().load()
    return result
  },

  healStaleSchedule: async () => {
    const state = get()
    const policy = state.draft.cash_policy
    const model = state.model
    if (!policy?.enabled || !model) {
      return
    }
    if (!isScheduleStale(policy, model)) {
      return
    }
    const from = policy.distribution.split_schedule?.basis.last_dist_num ?? null
    const to = model.last_dist_num ?? null
    const prefOnly = isPrefAccrualOnlyStale(policy, model)
    const notice = {
      from,
      to,
      healed: false,
      ...(prefOnly ? { reason: 'pref_accrual' as const } : {}),
    }
    if (state.dirty || !basisMatchesExceptCalendar(policy, model)) {
      set({ scheduleHealedNotice: notice })
      return
    }
    try {
      const response = await computeWaterfallSchedule(
        waterfallScheduleRequestFromPolicy(policy),
      )
      set((current) =>
        applyDraftMutation(current, (draft) => {
          if (!draft.cash_policy) {
            return
          }
          draft.cash_policy.distribution.split_schedule = parseApiCashPolicySplitSchedule(
            response.split_schedule,
          )
        }),
      )
      set({
        scheduleHealedNotice: {
          from,
          to,
          healed: true,
          ...(prefOnly ? { reason: 'pref_accrual' as const } : {}),
        },
      })
    } catch {
      set({ scheduleHealedNotice: notice })
    }
  },

  refreshStaleSchedule: async () => {
    const state = get()
    const policy = state.draft.cash_policy
    const model = state.model
    if (!policy?.enabled || !model) {
      return
    }
    const from = policy.distribution.split_schedule?.basis.last_dist_num ?? null
    const to = model.last_dist_num ?? null
    const prefOnly = isPrefAccrualOnlyStale(policy, model)
    const response = await computeWaterfallSchedule(
      waterfallScheduleRequestFromPolicy(policy),
    )
    set((current) =>
      applyDraftMutation(current, (draft) => {
        if (!draft.cash_policy) {
          return
        }
        draft.cash_policy.distribution.split_schedule = parseApiCashPolicySplitSchedule(
          response.split_schedule,
        )
      }),
    )
    set({
      scheduleHealedNotice: {
        from,
        to,
        healed: true,
        ...(prefOnly ? { reason: 'pref_accrual' as const } : {}),
      },
    })
  },

  save: async (acknowledgeWarnings) => {
    const { selectedCase, draft, evaluated, pendingMeta, pendingNotes, selectedCaseId } = get()
    if (!selectedCase || !evaluated) {
      return { ok: false, kind: 'error', message: 'No case selected' }
    }
    set({ saving: true, loadError: null })
    try {
      const updated = await updateCase(selectedCase.id, {
        name: selectedCase.name,
        description:
          pendingMeta?.description !== undefined
            ? pendingMeta.description
            : selectedCase.description,
        expires_on:
          pendingMeta?.expires_on !== undefined ? pendingMeta.expires_on : selectedCase.expires_on,
        inputs: toApiInputs(draft),
        outputs: toApiOutputs(evaluated),
        kpis: toApiKpis(evaluated),
        expected_updated_at: selectedCase.updated_at,
        ...(acknowledgeWarnings ? { acknowledge_warnings: true } : {}),
      })
      const saved = toEngineInputs(updated.inputs)
      let cases: CaseListItem[] = get().cases
      try {
        cases = await listCases()
      } catch {
        // Save succeeded; case list refresh is best-effort.
      }
      const notesSnapshot = pendingNotes ? { ...pendingNotes } : null

      set({
        selectedCase: updated,
        saved,
        draft: cloneInputs(saved),
        dirty: false,
        cases,
        pendingMeta: null,
        pendingNotes: null,
        scheduleHealedNotice: null,
        saving: false,
      })

      if (notesSnapshot && selectedCaseId != null) {
        void writePendingNotesInBackground(
          selectedCaseId,
          notesSnapshot,
          () => get().sharedNotes,
          (notes) => set({ notes }),
        )
      }

      return { ok: true, case: updated }
    } catch (error) {
      const validation = isValidationError(error)
      if (validation) {
        set({ saving: false })
        return { ok: false, kind: 'validation', ...validation }
      }
      const concurrency = isConcurrencyConflict(error)
      if (concurrency) {
        set({ saving: false })
        return { ok: false, kind: 'concurrency', case: concurrency.case }
      }
      const message = formatSaveError(error)
      set({
        saving: false,
        loadError: message,
      })
      return { ok: false, kind: 'error', message }
    }
  },

  importFromCgm: async () => {
    set({ importing: true, loadError: null })
    try {
      const result = await importModel()
      await get().load()
      await get().healStaleSchedule()
      set({ importing: false, importBlock: null, importDrift: null })
      return { ok: true, result }
    } catch (error) {
      const blocked = isImportBlocked(error)
      if (blocked) {
        set({ importing: false, importBlock: blocked, importDrift: null })
        return { ok: false, kind: 'blocked', ...blocked }
      }
      const drift = isImportDrift(error)
      if (drift) {
        set({ importing: false, importDrift: drift })
        return { ok: false, kind: 'drift', diff: drift.diff }
      }
      set({
        importing: false,
        loadError: formatApiError(error, 'Failed to import'),
      })
      return { ok: false, kind: 'error', message: formatApiError(error, 'Failed to import') }
    }
  },

  clearImportDrift: () => set({ importDrift: null }),

  reloadFromServer: async () => {
    const { selectedCaseId } = get()
    if (selectedCaseId == null) {
      return
    }
    const selectedCase = await getCase(selectedCaseId)
    const saved = toEngineInputs(selectedCase.inputs)
    const draft = cloneInputs(saved)
    const state = get()
    const evaluated = recomputeEvaluated(state.schema, state.history, draft, state.model)
    set({
      selectedCase,
      saved,
      draft,
      dirty: false,
      evaluated,
      pendingMeta: null,
      pendingNotes: null,
    })
  },

  adoptServerTimestamp: async () => {
    const { selectedCaseId, draft } = get()
    if (selectedCaseId == null) {
      return
    }
    const selectedCase = await getCase(selectedCaseId)
    const saved = toEngineInputs(selectedCase.inputs)
    const state = get()
    const evaluated = recomputeEvaluated(state.schema, state.history, draft, state.model)
    set({
      selectedCase,
      saved,
      dirty: isDirty(state, draft),
      evaluated,
    })
  },

  upsertNote: async (cellKey, text) => {
    const { selectedCaseId, notes, sharedNotes } = get()
    const shared = isSharedNoteCellKey(cellKey)
    if (!shared && selectedCaseId == null) {
      return { ok: false, message: 'No case selected' }
    }
    const previous = notes[cellKey]
    const trimmed = text.trim()
    const nextNotes = { ...notes }
    const nextSharedNotes = { ...sharedNotes }
    if (trimmed) {
      nextNotes[cellKey] = trimmed
      if (shared) {
        nextSharedNotes[cellKey] = trimmed
      }
    } else {
      delete nextNotes[cellKey]
      if (shared) {
        delete nextSharedNotes[cellKey]
      }
    }
    set(shared ? { notes: nextNotes, sharedNotes: nextSharedNotes } : { notes: nextNotes })
    try {
      if (shared) {
        await upsertSharedNoteApi(cellKey, text)
      } else {
        await upsertNoteApi(selectedCaseId!, cellKey, text)
      }
      return { ok: true }
    } catch (error) {
      const rollback = { ...notes }
      const rollbackShared = { ...sharedNotes }
      if (previous !== undefined) {
        rollback[cellKey] = previous
        if (shared) {
          rollbackShared[cellKey] = previous
        }
      } else {
        delete rollback[cellKey]
        if (shared) {
          delete rollbackShared[cellKey]
        }
      }
      set(shared ? { notes: rollback, sharedNotes: rollbackShared } : { notes: rollback })
      return { ok: false, message: formatApiError(error, 'Failed to save note') }
    }
  },

  refreshExports: async (caseId) => {
    const lastExport = await loadLastExportForCase(caseId)
    set({ lastExport })
  },

  loadCaseFromFile: async (parsed) => {
    const errorIssues = parsed.issues.filter((issue) => issue.severity === 'error')
    if (
      parsed.inputs == null ||
      parsed.target.kind === 'blocked' ||
      errorIssues.length > 0
    ) {
      const message =
        errorIssues.map((issue) => issue.message).join(' ') ||
        'This file cannot be loaded.'
      return { ok: false, kind: 'error', message }
    }

    const caseName = parsed.file?.case.name?.trim() ?? ''
    if (parsed.target.kind === 'create') {
      const createResult = await get().createCase(caseName)
      if (!createResult.ok) {
        return createResult
      }
    } else if (parsed.target.kind === 'override') {
      const overrideName = parsed.target.name
      const target = get().cases.find((caseItem) => caseItem.name === overrideName)
      if (!target) {
        return { ok: false, kind: 'error', message: `Case "${overrideName}" was not found.` }
      }
      await get().selectCase(target.id)
    }

    const state = get()
    const { policy: hydratedPolicy, problems: hydrationProblems } = await hydrateCashPolicy(
      parsed.inputs.cash_policy,
      evaluateContextFromModel(state.model, parsed.inputs.cash_policy),
      { computeWaterfallSchedule, getYieldCurve },
    )

    const hydratedInputs = structuredClone(parsed.inputs)
    if (hydratedPolicy) {
      hydratedInputs.cash_policy = hydratedPolicy
    }

    const selectedCase = get().selectedCase
    let pendingMeta: ProjectionsState['pendingMeta'] = null
    if (selectedCase) {
      const metaPatch: { description?: string; expires_on?: string | null } = {}
      const fileDescription = parsed.meta.description?.trim() ?? ''
      if (fileDescription && fileDescription !== (selectedCase.description ?? '')) {
        metaPatch.description = fileDescription
      }
      if (
        parsed.meta.expires_on != null &&
        parsed.meta.expires_on !== selectedCase.expires_on
      ) {
        metaPatch.expires_on = parsed.meta.expires_on
      }
      if (Object.keys(metaPatch).length > 0) {
        pendingMeta = metaPatch
      }
    }

    const currentNotes = get().notes
    const pendingNoteEntries = Object.entries(parsed.notes).filter(
      ([key, text]) => !isSharedNoteCellKey(key) && text !== (currentNotes[key] ?? ''),
    )
    const pendingNotes =
      pendingNoteEntries.length > 0 ? Object.fromEntries(pendingNoteEntries) : null

    set((current) => {
      const withPending = { ...current, pendingMeta, pendingNotes }
      const nextInputs = structuredClone(hydratedInputs)
      return {
        ...applyDraftMutation(withPending, (draft) => {
          replaceDraftInputs(draft, nextInputs)
        }),
        pendingMeta,
        pendingNotes,
      }
    })

    return { ok: true, hydrationProblems }
  },
}))
